



















































































































































































































































































































































































import { Component, Vue } from 'vue-property-decorator'
import {
  pageRecords,
  createRecord,
  updateRecord,
  deleteRecord,
  markRecordLaunched,
  markRecordChanged,
  getRecordStats,
  listRecordProducts,
  createProduct,
  renameProduct,
  deleteProduct,
  PersonalRecord,
  RecordStatus,
  RecordStats,
  STATUS_MAP,
  inferStatus as calcRecordStatus
} from '@/api/record'

interface KanbanCol {
  key: RecordStatus
  title: string
  color: string
  bgColor: string
  headBg: string
  emptyText: string
  items: PersonalRecord[]
  total: number
  hasMore: boolean
  loading: boolean
}

interface ColState {
  items: PersonalRecord[]
  page: number
  total: number
  loading: boolean
}

const COLUMN_DEFS: Omit<KanbanCol, 'items' | 'total' | 'hasMore' | 'loading'>[] = [
  { key: 'pending', title: '待上线', color: '#d97706', bgColor: '#fffbeb', headBg: '#fef3c7', emptyText: '暂无待发版任务' },
  { key: 'normal', title: '正常上线', color: '#059669', bgColor: '#ecfdf5', headBg: '#d1fae5', emptyText: '暂无正常上线记录' },
  { key: 'overdue', title: '逾期上线', color: '#dc2626', bgColor: '#fff1f2', headBg: '#fecdd3', emptyText: '暂无逾期上线记录' },
  { key: 'changed', title: '任务变更', color: '#7c3aed', bgColor: '#f5f3ff', headBg: '#ddd6fe', emptyText: '暂无变更任务' }
]

type ViewMode = 'kanban' | 'list'

@Component({ name: 'PersonalRecordPage' })
export default class PersonalRecordPage extends Vue {
  private STATUS_MAP = STATUS_MAP
  private viewMode: ViewMode = 'list'
  private colPageSize = 20
  private listPage = 1
  private listPageSize = 20
  private listTotal = 0
  private listItems: PersonalRecord[] = []
  private listLoading = false
  private tableHeight = 480
  private tableResizeObserver: ResizeObserver | null = null
  private loading = false
  private saving = false
  private formVisible = false
  private changeVisible = false
  private changeSaving = false
  private changeRemark = ''
  private changeTarget: PersonalRecord | null = null
  private products: string[] = []
  private keyword = ''
  private productFilter = ''
  private statusFilter: RecordStatus | '' = ''
  private dateRange: string[] = []
  private stats: RecordStats = { total: 0, pending: 0, normal: 0, overdue: 0, changed: 0 }
  private colData: Record<RecordStatus, ColState> = {
    pending: { items: [], page: 1, total: 0, loading: false },
    normal: { items: [], page: 1, total: 0, loading: false },
    overdue: { items: [], page: 1, total: 0, loading: false },
    changed: { items: [], page: 1, total: 0, loading: false }
  }

  private form: PersonalRecord = this.emptyForm()

  private rules = {
    product: [{ required: true, message: '请输入产品', trigger: 'change' }],
    title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
  }

  get columns(): KanbanCol[] {
    return COLUMN_DEFS.map(col => {
      const state = this.colData[col.key]
      return {
        ...col,
        items: state.items,
        total: state.total,
        hasMore: state.items.length < state.total,
        loading: state.loading
      }
    })
  }

  /** 看板模式下按状态筛选时只展示对应列 */
  get displayColumns(): KanbanCol[] {
    if (!this.statusFilter) return this.columns
    return this.columns.filter(col => col.key === this.statusFilter)
  }

  /** 表单内根据日期预览状态 */
  get previewStatus(): RecordStatus | null {
    if (this.form.launchDate) {
      return calcRecordStatus({
        launchDate: this.form.launchDate,
        expectedLaunchDate: this.form.expectedLaunchDate
      })
    }
    if (this.form.status === 'changed') return 'changed'
    return null
  }

  created() {
    const savedMode = localStorage.getItem('recordViewMode') as ViewMode | null
    if (savedMode === 'kanban' || savedMode === 'list') {
      this.viewMode = savedMode
    }
    const savedSize = localStorage.getItem('recordColPageSize')
    if (savedSize) {
      const n = parseInt(savedSize, 10)
      if (!Number.isNaN(n)) this.colPageSize = n
    }
    this.reloadData()
    this.loadProducts()
  }

  mounted() {
    this.setupTableHeightObserver()
  }

  beforeDestroy() {
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect()
      this.tableResizeObserver = null
    }
  }

  private setupTableHeightObserver() {
    this.$nextTick(() => {
      const body = this.$el.querySelector('.board-body') as HTMLElement | null
      if (!body || typeof ResizeObserver === 'undefined') {
        this.updateTableHeight()
        return
      }
      this.tableResizeObserver = new ResizeObserver(() => this.updateTableHeight())
      this.tableResizeObserver.observe(body)
      this.updateTableHeight()
    })
  }

  private updateTableHeight() {
    this.$nextTick(() => {
      const body = this.$el.querySelector('.board-body') as HTMLElement | null
      const pagination = this.$el.querySelector('.list-pagination') as HTMLElement | null
      if (!body || this.viewMode !== 'list') return
      const paginationH = pagination ? pagination.offsetHeight : 52
      this.tableHeight = Math.max(240, body.clientHeight - paginationH)
    })
  }

  private emptyForm(): PersonalRecord {
    return { id: undefined, product: '', title: '', version: '', expectedLaunchDate: '', launchDate: '' }
  }

  private async loadProducts() {
    try {
      this.products = await listRecordProducts()
    } catch (e) { /* handled */ }
  }

  /** 从当前列表数据补全产品，并自动同步到产品表 */
  private async mergeProductsFromRecords() {
    const fromApi = new Set<string>(this.products)
    const fromRecords = new Set<string>()
    if (this.viewMode === 'list') {
      this.listItems.forEach(r => { if (r.product) fromRecords.add(r.product) })
    } else {
      const statuses: RecordStatus[] = ['pending', 'normal', 'overdue', 'changed']
      statuses.forEach(s => {
        this.colData[s].items.forEach(r => { if (r.product) fromRecords.add(r.product) })
      })
    }
    const all = new Set<string>([...fromApi, ...fromRecords])
    this.products = Array.from(all).sort((a, b) => a.localeCompare(b, 'zh-CN'))

    const toSync = Array.from(fromRecords).filter(p => !fromApi.has(p))
    if (!toSync.length) return
    await Promise.all(toSync.map(name => createProduct(name).catch(() => {})))
    try {
      const latest = await listRecordProducts()
      if (latest && latest.length) this.products = latest
    } catch (e) { /* handled */ }
  }

  private handleAddProduct() {
    this.$prompt('请输入产品名称', '添加产品', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPattern: /\S+/,
      inputErrorMessage: '产品名称不能为空'
    }).then(async ({ value }: any) => {
      const name = (value as string).trim()
      await createProduct(name)
      this.$message.success('产品已添加')
      await this.loadProducts()
    }).catch(() => {})
  }

  private handleEditProduct(name: string) {
    this.$prompt('请输入新的产品名称', '重命名产品', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: name,
      inputPattern: /\S+/,
      inputErrorMessage: '产品名称不能为空'
    }).then(async ({ value }: any) => {
      const newName = (value as string).trim()
      if (newName === name) return
      await renameProduct(name, newName)
      this.$message.success('产品已重命名')
      if (this.form.product === name) this.form.product = newName
      if (this.productFilter === name) this.productFilter = newName
      await Promise.all([this.loadProducts(), this.reloadData()])
    }).catch(() => {})
  }

  private handleDeleteProduct(name: string) {
    this.$confirm(
      `确定删除产品「${name}」？将同时删除该产品下的全部发版记录，且不可恢复。`,
      '删除产品',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    ).then(async () => {
      await deleteProduct(name)
      this.$message.success('产品已删除')
      if (this.form.product === name) this.form.product = ''
      if (this.productFilter === name) this.productFilter = ''
      await Promise.all([this.loadProducts(), this.reloadData()])
    }).catch(() => {})
  }

  private async loadStats() {
    try {
      this.stats = await getRecordStats()
    } catch (e) { /* handled */ }
  }

  private effectiveColPageSize(): number {
    return this.colPageSize === -1 ? 100 : this.colPageSize
  }

  private buildQuery(status: RecordStatus | undefined, page: number, pageSize?: number) {
    const params: any = {
      page,
      pageSize: pageSize != null ? pageSize : this.effectiveColPageSize(),
      keyword: this.keyword || undefined,
      product: this.productFilter || undefined
    }
    if (status) params.status = status
    if (this.dateRange && this.dateRange.length === 2) {
      params.startDate = this.dateRange[0]
      params.endDate = this.dateRange[1]
    }
    return params
  }

  private buildListQuery(page: number) {
    return this.buildQuery(this.statusFilter || undefined, page, this.listPageSize)
  }

  private async loadColumnAll(status: RecordStatus) {
    const state = this.colData[status]
    state.loading = true
    try {
      const batch = 100
      let page = 1
      let all: PersonalRecord[] = []
      let total = 0
      do {
        const res = await pageRecords(this.buildQuery(status, page, batch))
        all = all.concat(res.records || [])
        total = res.total || 0
        page++
      } while (all.length < total)
      state.items = all
      state.page = page - 1
      state.total = total
    } finally {
      state.loading = false
    }
  }

  private async loadColumn(status: RecordStatus, append = false) {
    if (this.colPageSize === -1 && !append) {
      await this.loadColumnAll(status)
      return
    }
    const state = this.colData[status]
    state.loading = true
    try {
      const page = append ? state.page + 1 : 1
      const res = await pageRecords(this.buildQuery(status, page))
      const records = res.records || []
      state.items = append ? state.items.concat(records) : records
      state.page = page
      state.total = res.total || 0
    } finally {
      state.loading = false
    }
  }

  private async loadBoard() {
    this.loading = true
    try {
      const statuses: RecordStatus[] = this.statusFilter
        ? [this.statusFilter]
        : ['pending', 'normal', 'overdue', 'changed']
      await Promise.all([
        ...statuses.map(s => this.loadColumn(s, false)),
        this.loadStats()
      ])
      await this.mergeProductsFromRecords()
    } finally {
      this.loading = false
    }
  }

  private async loadList() {
    this.listLoading = true
    try {
      const res = await pageRecords(this.buildListQuery(this.listPage))
      this.listItems = res.records || []
      this.listTotal = res.total || 0
      await this.loadStats()
      await this.mergeProductsFromRecords()
    } finally {
      this.listLoading = false
      this.updateTableHeight()
    }
  }

  private reloadData() {
    if (this.viewMode === 'list') {
      return this.loadList()
    }
    return this.loadBoard()
  }

  private onViewModeChange() {
    localStorage.setItem('recordViewMode', this.viewMode)
    if (this.viewMode === 'list') this.listPage = 1
    this.reloadData()
    this.$nextTick(() => this.updateTableHeight())
  }

  private onPageSizeChange() {
    localStorage.setItem('recordColPageSize', String(this.colPageSize))
    this.reloadData()
  }

  private onListPageChange(page: number) {
    this.listPage = page
    this.loadList()
  }

  private onListPageSizeChange(size: number) {
    this.listPageSize = size
    this.listPage = 1
    this.loadList()
  }

  private loadMore(status: RecordStatus) {
    if (this.colData[status].loading) return
    this.loadColumn(status, true)
  }

  /** 看板区域统一滚动，滚到底自动加载各列更多数据 */
  private onBoardBodyScroll(e: Event) {
    if (this.viewMode !== 'kanban') return
    const el = e.target as HTMLElement
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return
    if (this.colPageSize === -1) return
    this.displayColumns.forEach(col => {
      if (col.hasMore && !col.loading) {
        this.loadMore(col.key)
      }
    })
  }

  private setProduct(p: string) {
    this.productFilter = p
    if (this.viewMode === 'list') this.listPage = 1
    this.reloadData()
  }

  private setStatus(status: RecordStatus | '') {
    if (this.statusFilter === status) return
    this.statusFilter = status
    if (this.viewMode === 'list') {
      this.listPage = 1
    } else {
      ;(['pending', 'normal', 'overdue', 'changed'] as RecordStatus[]).forEach(s => {
        this.colData[s].items = []
        this.colData[s].page = 1
        this.colData[s].total = 0
      })
    }
    this.reloadData()
  }

  private clearSearch() {
    this.keyword = ''
    if (this.viewMode === 'list') this.listPage = 1
    this.reloadData()
  }

  private openForm(row?: PersonalRecord) {
    if (row) {
      this.form = {
        ...row,
        version: row.version || '',
        expectedLaunchDate: row.expectedLaunchDate || '',
        launchDate: row.launchDate || ''
      }
    } else {
      this.form = this.emptyForm()
    }
    this.formVisible = true
    this.$nextTick(() => {
      const formRef = this.$refs.form as any
      if (formRef) formRef.clearValidate()
    })
  }

  private handleSave() {
    const formRef = this.$refs.form as any
    formRef.validate(async (valid: boolean) => {
      if (!valid) return
      this.saving = true
      try {
        const payload = {
          product: this.form.product,
          title: this.form.title,
          version: this.form.version || undefined,
          expectedLaunchDate: this.form.expectedLaunchDate || undefined,
          launchDate: this.form.launchDate || undefined
        }
        if (this.form.id) {
          await updateRecord(this.form.id, payload)
        } else {
          await createRecord(payload)
        }
        this.$message.success('已保存')
        this.formVisible = false
        await Promise.all([this.reloadData(), this.loadProducts()])
      } finally {
        this.saving = false
      }
    })
  }

  private handleMarkLaunched(row: PersonalRecord) {
    if (!row.id) return
    this.$confirm(`将「${row.title}」标记为今日上线？`, '', { confirmButtonText: '确认', cancelButtonText: '取消', type: 'info' })
      .then(async () => {
        await markRecordLaunched(row.id as number)
        this.$message.success('已上线')
        await this.reloadData()
      })
      .catch(() => {})
  }

  private handleMarkChanged(row: PersonalRecord) {
    this.changeTarget = row
    this.changeRemark = ''
    this.changeVisible = true
  }

  private async submitChange() {
    if (!this.changeTarget || !this.changeTarget.id) return
    if (!this.changeRemark.trim()) {
      this.$message.warning('请填写变更备注')
      return
    }
    this.changeSaving = true
    try {
      await markRecordChanged(this.changeTarget.id, this.changeRemark.trim())
      this.$message.success('已标记为任务变更')
      this.changeVisible = false
      await this.reloadData()
    } finally {
      this.changeSaving = false
    }
  }

  private handleDelete(row: PersonalRecord) {
    if (!row.id) return
    this.$confirm(`删除「${row.title}」？`, '', { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' })
      .then(async () => {
        await deleteRecord(row.id as number)
        this.$message.success('已删除')
        await Promise.all([this.reloadData(), this.loadProducts()])
      })
      .catch(() => {})
  }

  private formatDate(v?: string) {
    if (!v) return ''
    const p = v.slice(0, 10).split('-')
    return p.length === 3 ? `${p[1]}/${p[2]}` : v
  }

  private inferStatus(item: PersonalRecord) {
    return calcRecordStatus(item)
  }

  private isOverdueLaunch(item: PersonalRecord) {
    return calcRecordStatus(item) === 'overdue'
  }
}
